import { type ComponentType, type LazyExoticComponent, lazy } from 'react';
import { CLIENT_MODULES } from '@/modules';
import type {
  ClientModule,
  ClientModuleName,
  HeaderAction,
  HomeCard,
  Lazy,
  MeSection,
  ModuleRoute,
  PlayHubTile,
  PlaySection,
  ProfileSection,
  SlotEntry,
  SlotName,
} from './module';

/** Read-side helpers over the frozen module registry (client/src/modules/index.ts). */

type Owned<T> = T & { module: ClientModuleName };

function collect<T extends { order?: number }>(
  pick: (m: ClientModule) => readonly T[] | undefined,
  modules: readonly ClientModule[] = CLIENT_MODULES,
): Owned<T>[] {
  const out: Owned<T>[] = [];
  for (const m of modules) for (const item of pick(m) ?? []) out.push({ ...item, module: m.name });
  return out.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export const moduleRoutes = (mods?: readonly ClientModule[]): Owned<ModuleRoute>[] => {
  const out: Owned<ModuleRoute>[] = [];
  for (const m of mods ?? CLIENT_MODULES) for (const r of m.routes ?? []) out.push({ ...r, module: m.name });
  return out;
};
export const homeCards = (mods?: readonly ClientModule[]): Owned<HomeCard>[] => collect((m) => m.homeCards, mods);
export const playHubTiles = (mods?: readonly ClientModule[]): Owned<PlayHubTile>[] =>
  collect((m) => m.playHubTiles, mods);
export const playSections = (mods?: readonly ClientModule[]): Owned<PlaySection>[] =>
  collect((m) => m.playSections, mods);
export const meSections = (mods?: readonly ClientModule[]): Owned<MeSection>[] => collect((m) => m.meSections, mods);
export const profileSections = (mods?: readonly ClientModule[]): Owned<ProfileSection>[] =>
  collect((m) => m.profileSections, mods);
export const headerActions = (mods?: readonly ClientModule[]): Owned<HeaderAction>[] =>
  collect((m) => m.headerActions, mods);

export function slotEntries<N extends SlotName>(name: N, mods?: readonly ClientModule[]): Owned<SlotEntry<N>>[] {
  const out: Owned<SlotEntry<N>>[] = [];
  for (const m of mods ?? CLIENT_MODULES) {
    const e = m.slots?.[name] as SlotEntry<N> | undefined;
    if (e) out.push({ ...e, module: m.name });
  }
  return out.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const lazyCache = new WeakMap<object, unknown>();

/** One React.lazy per loader, so re-renders never remount a module part. */
export function lazyPart<P>(loader: Lazy<P>): LazyExoticComponent<ComponentType<P>> {
  let c = lazyCache.get(loader) as LazyExoticComponent<ComponentType<P>> | undefined;
  if (!c) {
    c = lazy(loader);
    lazyCache.set(loader, c);
  }
  return c;
}

let inited = false;
/** Calls every module's init() once, after sign-in. A throwing module does not stop the rest. */
export function initModules(): void {
  if (inited) return;
  inited = true;
  for (const m of CLIENT_MODULES) {
    try {
      m.init?.();
    } catch (e) {
      console.error(`[${m.name}] init failed`, e);
    }
  }
}
